import React, { useState, useEffect, useCallback } from 'react';
import { connect } from 'react-redux';
import AuthHOC from '../Auth';
import Header from '../../components/Header';
import ConfirmModal from '../../components/modals/common/ConfirmModal';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { Stack, Button, Typography, Alert, CircularProgress } from "@mui/material";
import UserGroupForm from '../../components/forms/UserGroupForm';
import { getAllAvailableLanguages } from '../../services/languagesService';
import { getAll as getAllUsers } from '../../services/userService';
import { getAll as getDocumentTypes } from '../../services/documentTypesService'
import { getX3Folders } from '../../services/x3Service';
import {
  create as createUserGroup, 
  getById as getUserGroupById,
  update as updateUserGroup
} from '../../services/userGroupsService';
import {
    levelDefinitions as permissionLevelDefinitions,
    categories as permissionCategories,
    levels as permissionLevels,
    LOCKED_LEVEL_BY_KEY as lockedLevelByKey
} from '../../permissions/permissionConfig'
import { 
    ACTIONS as docTypeActionsConfig,
    PRESETS as docTypePresetsConfig,
    presetToActions
} from '../../permissions/documentTypePermissionConfig'
import { 
    grantsToLevelsObject,
    levelsObjectToGrants
} from '../../permissions/permissionsMapper'

function UserGroupDetailsView(props) {
    // data
    const [availableX3Folders, setAvailableX3Folders] = useState([]);
    const [availableLanguages, setAvailableLanguages] = useState([]);
    const [availablePermissionCategories, setAvailablePermissionCategories] = useState([]);
    const [availablePermissionLevelDefinitions, setAvailablePermissionLevelDefinitions] = useState([]);
    const [documentTypes, setDocumentTypes] = useState([]);

    //state
    const [loggedInUser, setLoggedInUser] = useState(null);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState('');
    const [successMsg, setSuccessMsg] = useState('');
    const [warningMsg, setWarningMsg] = useState('');
    const [isDirty, setIsDirty] = useState(false);

    // loading / errors
    const [isLoading, setIsLoading] = useState(true);
    const [isLoadingAvailableX3Folders, setIsLoadingAvailableX3Folders] = useState(false);
    const [isLoadingDocumentTypes, setIsLoadingDocumentTypes] = useState(false);

    // modals
    const [showConfirmModal, setShowConfirmModal] = useState(false);

    const userGroupParameterId = props.match?.params?.userGroupParameterId;
    const isAdmin = props.userReducer?.userType?.toLowerCase() === 'admin';

    // Debug: Check what userType value we're getting
    console.log('UserGroupDetailsView - userType:', props.userReducer?.userType, 'isAdmin:', isAdmin);

    const isEditMode = !!userGroupParameterId;

    const [formData, setFormData] = useState({
        id: 0,
        name: '',
        description: '',
        x3Folders: '',
        isActive: false,
        audit: [],
        userMembers: [],
        permissionLevels: {},
        documentTypes: [],
        documentTypePermissions: {}
    });

    // Redirect non-admin users
    useEffect(() => {
        if (props.userReducer?.userType && !isAdmin) {
            setError('Access denied. Only administrators can create or edit user groups.');
            const timer = setTimeout(() => {
                props.history.push('/site/administration/UserGroups');
            }, 2000);
            return () => clearTimeout(timer);
        }
    }, [isAdmin, props.userReducer?.userType, props.history]);

    useEffect(() => {
        let cancelled = false;

        // Get user details for audit
        const username = props.userReducer?.user;
        const userId = props.userReducer?.userId;

        // If we have userId in Redux, use it directly (no API call)
        if (username && userId) {
            setLoggedInUser({
                id: userId,
                username: username
            });
            return;
        }

        // Fallback: fetch user from API if userId not in Redux (e.g., after page refresh)
        const loadLoggedInUser = async () => {
            try {
                if (!username) return;

                const result = await getAllUsers({ username: username });
                if (cancelled) return;

                if (!result.ok) {
                    setError(`Could not retrieve logged in user (status ${result.status})`);
                    return;
                }

                setLoggedInUser(result.data?.[0] || null);
            } catch (error) {
                if (!cancelled) {
                    setError("Could not retrieve currently logged in user.");
                }
            }
        };

        loadLoggedInUser();

        return () => {
            cancelled = true;
        };
    }, [props.userReducer?.user, props.userReducer?.userId])

      useEffect(() => {
        setAvailablePermissionCategories(permissionCategories);
        setAvailablePermissionLevelDefinitions(permissionLevelDefinitions);
      }, []);

      useEffect(() => {
        let cancelled = false;

        const load = async () => {
            try {
                setIsLoadingAvailableX3Folders(true);
                setIsLoadingDocumentTypes(true);

                const [x3FoldersRes, docTypesResponse, languagesRes] = await Promise.all([
                    getX3Folders(),
                    getDocumentTypes(),
                    getAllAvailableLanguages()
                ]);

                if (cancelled) return;

                if (!x3FoldersRes.ok) {
                    setError('Error loading x3 folders. Error status: ' + x3FoldersRes.status + " OK: " + x3FoldersRes.ok);
                }
                else {
                    const mappedFolders = x3FoldersRes.data.map(item => ({
                        id: item,
                        name: item
                    }));                   

                    setAvailableX3Folders(mappedFolders || []);
                }

                if (!docTypesResponse.ok) {
                    setError('Error loading document types. Error status: ' + docTypesResponse.status + " OK: " + docTypesResponse.ok);
                }
                else {
                    const filteredDocumentTypes = docTypesResponse.data.map((dt) => ({
                        name: dt.DocumentType,
                        folder: dt.Folder
                    }));
                    setDocumentTypes(filteredDocumentTypes);
                }

                if (!languagesRes) {
                    setError('Error loading languages');
                }
                else {
                    console.log('languagesRes : ', languagesRes);
                    setAvailableLanguages(languagesRes || []);
                }
            } catch (err) {
                if (!cancelled) setError(`Failed to load reference data: ${err.message}`);
            }
            finally {
                if (!cancelled) {
                    setIsLoadingAvailableX3Folders(false);
                    setIsLoadingDocumentTypes(false);
                }
            }
        };

        load();

        return () => {
            cancelled = true;
        }
      }, []);

      useEffect(() => {
        let cancelled = false;

        const loadGroup = async () => {
            if (!isEditMode) {
                // In create mode, no data to load
                setIsLoading(false);
                return;
            }
            if (!userGroupParameterId) return;

            setIsLoading(true);

            try {
                const result = await getUserGroupById(userGroupParameterId);
                if (cancelled) return;

                if (!result.ok) {
                    setError('Error loading group details. Error status: ' + result.status + " OK: " + result.ok);
                    return;
                }

                const group = result.data;

                const x3Folders = Array.isArray(group.x3Folders) ? group.x3Folders.flat() : [];

                setFormData((prev) =>({
                    ...prev,
                    id: group.id || group._id,
                    name: group.name || '',
                    description: group.description || '',
                    language: group.language || '',
                    x3Folders: x3Folders.map((i) => ({ id: i, name: i })),
                    isActive: group.isActive || false,
                    audit: group.audit || {},
                    userMembers: group.memberIds,
                    permissionLevels: grantsToLevelsObject(group.permissions),
                    documentTypePermissions: group.documentTypePermissions
                }));
            } catch (err) {
                if (!cancelled) setError(`Failed to load group details: ${err.message}`);
            }
            finally {
                if (!cancelled) setIsLoading(false);
            }
        };

        loadGroup();

        return () => {
            cancelled = true;
        };
      }, [isEditMode, userGroupParameterId]);

    function getTitle() {
      const localisation = props.localisation || {};
      return localisation["menu_header_administration_user_groups"]
          ? localisation["menu_header_administration_user_groups"].element_lang
          : "Administration - User Group Details";
    };
    let title = getTitle();
    const handleGoBackClick = () => {
        if (isDirty) {
            setShowConfirmModal(true);
            return;
        }
        else {
            props.history.push('/site/administration/UserGroups');
        }
    }

    const handleConfirmGoBackClick = () => {
        setShowConfirmModal(false);
        props.history.push('/site/administration/UserGroups');
    }

    const handleCancelGoBackClick = () => {
        setShowConfirmModal(false);
    }

    const handleSubmit = async (formData) => {

        if (!loggedInUser) {
            setError("Cannot save: logged-in user not loaded yet.");
            return;
        }
        setSaving(true);
        try {
            const permissions = levelsObjectToGrants(formData.permissionLevels);

            const userGroupObject = {
                id: formData.id,
                name: formData.name,
                description: formData.description,
                language: formData.language,
                isActive: formData.isActive,
                memberIds: formData.memberIds,
                x3Folders: (formData.x3Folders || []),
                lastUpdated: formData.lastUpdated,
                permissions: permissions,
                documentTypePermissions: formData.documentTypePermissions,
                audit: (isEditMode) ? {
                    createdBy: formData.audit.createdBy,
                    createdByName: formData.audit?.createdByName,
                    createdOn: formData.audit?.createdOn,
                    modifiedBy: loggedInUser.id,
                    modifiedByName: loggedInUser.username,
                    modifiedOn: new Date()
                } : {
                    createdBy: loggedInUser.id,
                    createdByName: loggedInUser.username,
                    createdOn: new Date(),
                    modifiedBy: 0,
                    modifiedByName: '',
                    modifiedOn: null
                }
            }

            if (isEditMode) {
                await updateUserGroup(userGroupParameterId, userGroupObject);
            }
            else {
                await createUserGroup(userGroupObject);
            }

            props.history.push('/site/administration/userGroups');
        } catch (err) {
            setError('Failed to save user group. Error: ' + err.message);
        }
        finally {
            setSaving(false);
        }
    }

    return (
        <div>
            <Header visible={!props.parsed.X3} />
            <div className="page-wrapper">
                <div className="s-page">
                    <div className="s-page-header">
                        <Stack 
                            spacing={0.5} 
                            alignItems="flex-start"
                            sx={{ mb: 3, textAlign: "left" }}
                        >
                            <Typography variant="body2" color="text.secondary">
                                {title} 
                            </Typography>
                            <Typography variant="h5" sx={{ fontWeight: 600 }}>
                                {isEditMode ? 'Edit User Group': 'Create New User Group'}
                            </Typography>
                            <Typography variant="body1" color="text.secondary">
                                Manage user groups, permissions and document permissions.
                            </Typography>
                        </Stack>
                        <Button
                            variant='outlined'
                            type="button"
                            startIcon={<ArrowBackIcon/>}
                            onClick={handleGoBackClick}
                            sx={{
                                textTransform: 'none',
                                color: 'text.secondary',
                                borderColor: 'divider',
                                '&:hover': {
                                    color: 'primary.main',
                                    borderColor: 'primary.main'
                                }
                            }}
                        >
                            Back to Groups
                        </Button>
                    </div>
                    <div>
                        {error && (
                            <Alert severity="error" sx={{ mb: 2 }}>
                            {error}
                            </Alert>
                        )}
                        {successMsg && (
                            <Alert severity="success" sx={{ mb: 2 }}>
                            {successMsg}
                            </Alert>
                        )}
                        {warningMsg && (
                            <Alert severity="warning" sx={{ mb: 2 }}>
                            {warningMsg}
                            </Alert>
                        )}

                        {/* Loading Status */}
                        {(isLoadingAvailableX3Folders || isLoadingDocumentTypes || (isLoading && isEditMode)) && (
                            <Alert
                                severity="info"
                                sx={{ mb: 2 }}
                                icon={<CircularProgress size={20} />}
                            >
                                <Stack spacing={0.5}>
                                    <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                        Loading data...
                                    </Typography>
                                    <Stack direction="row" spacing={2} sx={{ fontSize: '0.875rem' }}>
                                        {isLoadingAvailableX3Folders && (
                                            <Typography variant="caption" color="text.secondary">
                                                • Loading X3 folders
                                            </Typography>
                                        )}
                                        {isLoadingDocumentTypes && (
                                            <Typography variant="caption" color="text.secondary">
                                                • Loading document types
                                            </Typography>
                                        )}
                                        {isLoading && isEditMode && (
                                            <Typography variant="caption" color="text.secondary">
                                                • Loading user group details
                                            </Typography>
                                        )}
                                    </Stack>
                                </Stack>
                            </Alert>
                        )}
                        <div className="s-content-panel">
                        {isAdmin ? (
                            <UserGroupForm
                                mode={isEditMode ? 'edit' : 'create'}
                                isLoading={isLoading}
                                isLoadingAvailableX3Folders={isLoadingAvailableX3Folders}
                                isLoadingDocumentTypes={isLoadingDocumentTypes}
                                availableLanguages={availableLanguages}
                                saving={saving}
                                initialValues={isEditMode ? formData : null}
                                onSubmit={handleSubmit}
                                availableX3Folders={availableX3Folders}
                                availablePermissionCategories={availablePermissionCategories}
                                permissionLevelDefinitions={availablePermissionLevelDefinitions}
                                permissionLevels={permissionLevels}
                                lockedLevelByKey={lockedLevelByKey}
                                documentTypes={documentTypes}
                                docTypeActionsConfig={docTypeActionsConfig}
                                docTypePresetsConfig={docTypePresetsConfig}
                                presetToActions={presetToActions}
                                onDirtyChange={setIsDirty}
                            />
                        ) : (
                            <Alert severity="error" sx={{ mt: 2 }}>
                                You do not have permission to access this page. Redirecting...
                            </Alert>
                        )}
                        </div>
                    </div>
                    <ConfirmModal
                        isOpen={showConfirmModal}
                        mainTitle="Unsaved changes - Are you absolutely sure?"
                        mainMessage="There are unsaved changes, are you sure you want to go back? This action cannot be undone."
                        textConfirm="CONFIRM"
                        textCancel="CANCEL"
                        onConfirm={handleConfirmGoBackClick}
                        onCancel={handleCancelGoBackClick}
                        >
                    </ConfirmModal>
                </div>
            </div>
        </div>
    );
}
export default connect(function (state) { return state; })(
  AuthHOC(UserGroupDetailsView, { name: 'administration' })
);